import { useMutation, useQuery, useQueryClient } from 'react-query';
import { openerOrchestratorAxiosApi } from 'src/Config/axiosApi';
import { markGroupAccountUnresolved } from './groupAccountResolution';

/**
 * Engine pod tier vocabulary. `startOpenerGroup` may post a `tierOverride`
 * to force the engine pod's size; the groups-state payload reports the
 * running tier (`observedTier`) and what auto would pick (`recommendedTier`).
 */
export type EngineTier =
  | 'xs'
  | 's'
  | 'm'
  | 'l'
  | 'xl'
  | 'xxl'
  | 'huge'
  | 'mega';
export const ENGINE_TIERS: EngineTier[] = [
  'xs',
  's',
  'm',
  'l',
  'xl',
  'xxl',
  'huge',
  'mega'
];

/**
 * Per-group opener pod lifecycle — the opener-orchestrator service (P2:
 * split out of prediction-manager) orchestrates a dedicated engine pod +
 * Socket.io feed pod per pair group. These hooks cover the group-scoped
 * start/stop/panic controls and the pod-state poll that feeds the status
 * chips + the WS feed-target resolution, via `openerOrchestratorAxiosApi`.
 *
 * Wire contract (GET /api/system/opener/groups/state) is fail-soft: when
 * the cluster is unreachable the k8s-derived fields (`engineReplicas`,
 * `engineReady`, `feedReady`, `feedPath`) may be omitted — consumers must
 * treat a missing field as NOT ready.
 */
export interface OpenerGroupPodState {
  groupId: string;
  name: string;
  podsDesired: 'on' | 'off' | 'panicked';
  /**
   * The set this group would boot with RIGHT NOW (post-chain, kill switch
   * applied). `[]` means legacy; OMITTED when the backend could not read the
   * global layer — the same "we cannot say" encoding as `bootFeatureFlags`,
   * and `hasPendingFlagRestart` refuses to draw a chip from either being
   * missing.
   */
  resolvedFeatureFlags?: string[];
  /**
   * The set the RUNNING engine Deployment carries. OMITTED when there is no
   * engine or k8s is unreachable — which is NOT `[]`, and no drift chip may be
   * drawn from a missing value.
   */
  bootFeatureFlags?: string[];
  engineReplicas?: number;
  engineReady?: boolean;
  feedReady?: boolean;
  feedPath?: string;
  /**
   * Expected engine leg-units (tokio tasks) for the group's active rules — the
   * real load gauge. Omitted when the group has no running engine or before the
   * server's reconcile has computed it.
   */
  expectedUnits?: number;
  /**
   * Leg-unit ceiling of the RUNNING engine tier. The chip derives the % from
   * `expectedUnits / ceilingUnits` locally (the server also sends a `capacityPct`
   * on the wire, but the FE recomputes it — no separate field needed here).
   */
  ceilingUnits?: number;
  /** Running engine tier of the group's pod, when known. */
  observedTier?: EngineTier;
  /** Tier the auto-sizer would pick for the group, when known. */
  recommendedTier?: EngineTier;
}

export interface OpenerGroupsPodStateResponse {
  groups: OpenerGroupPodState[];
}

export const OPENER_GROUP_POD_STATE_KEY = ['opener-group-pod-state'];

export async function fetchOpenerGroupsPodState(): Promise<
  OpenerGroupPodState[]
> {
  const { data } =
    await openerOrchestratorAxiosApi.get<OpenerGroupsPodStateResponse>(
      '/api/system/opener/groups/state'
    );
  return Array.isArray(data?.groups) ? data.groups : [];
}

/**
 * Poll the per-group pod state every 5 s. react-query's default
 * `refetchIntervalInBackground: false` pauses the poll while the tab is
 * not focused, so this only ticks while the operator is looking.
 */
export function useOpenerGroupsPodState(opts?: { enabled?: boolean }) {
  return useQuery<OpenerGroupPodState[]>(
    OPENER_GROUP_POD_STATE_KEY,
    fetchOpenerGroupsPodState,
    {
      enabled: opts?.enabled ?? true,
      refetchInterval: 5_000,
      keepPreviousData: true
    }
  );
}

/* ------------------------------------------------------------------ */
/* Request functions (exported for wire-shape tests)                   */
/* ------------------------------------------------------------------ */

/**
 * The two `venue_accounts` ids a start may put a group on. Taken as a PAIR and
 * never as two independent optionals: a caller that supplies one half is
 * choosing an account for one venue and inheriting the other's silently, which
 * is the reading no operator can verify from the button they pressed.
 */
export interface StartOpenerGroupAccounts {
  kalshiAccountId: string;
  polymarketAccountId: string;
}

/**
 * Start-time overrides. Omit `accounts` to start the group on the pair it
 * already carries — the override never UNSETS an account.
 */
export interface StartOpenerGroupOptions {
  accounts?: StartOpenerGroupAccounts;
  liveMode?: boolean;
  tier?: EngineTier;
}

// Start can be refused with a 409/400 (global panic lockout armed, Kalshi
// key-slot pool exhausted, a group with no account pair) — the server message
// is surfaced by the global MutationCache.onError toast via
// `extractServerErrorMessage`.
export async function startOpenerGroup(
  groupId: string,
  options: StartOpenerGroupOptions = {}
) {
  const body: {
    kalshiAccountId?: string;
    polymarketAccountId?: string;
    liveMode?: boolean;
    tierOverride?: EngineTier;
  } = {};
  if (options.accounts) {
    body.kalshiAccountId = options.accounts.kalshiAccountId;
    body.polymarketAccountId = options.accounts.polymarketAccountId;
  }
  if (options.liveMode !== undefined) body.liveMode = options.liveMode;
  if (options.tier !== undefined) body.tierOverride = options.tier;
  const { data } = await openerOrchestratorAxiosApi.post(
    `/api/system/opener/groups/${groupId}/start`,
    Object.keys(body).length === 0 ? undefined : body
  );
  return data;
}

export async function stopOpenerGroup(groupId: string) {
  const { data } = await openerOrchestratorAxiosApi.post(
    `/api/system/opener/groups/${groupId}/stop`
  );
  return data;
}

export async function panicSoftOpenerGroup(groupId: string) {
  const { data } = await openerOrchestratorAxiosApi.post(
    `/api/system/opener/groups/${groupId}/panic-soft`
  );
  return data;
}

export async function panicOpenerGroup(groupId: string) {
  const { data } = await openerOrchestratorAxiosApi.post(
    `/api/system/opener/groups/${groupId}/panic`
  );
  return data;
}

/* ------------------------------------------------------------------ */
/* Mutations                                                           */
/* ------------------------------------------------------------------ */

/**
 * Re-mark the group, then force a FRESH group-list fetch.
 *
 * The mark is taken twice on purpose. `onMutate` covers the request's own
 * flight time; this second one postdates the server's write of the two account
 * ids, which happens partway through the request — a fetch that started after
 * the first mark but before that write would otherwise clear the guard with the
 * previous run's row. `cancelRefetch` drops any fetch already in flight so the
 * refetch this schedules is one that STARTED after the mark, which is the only
 * kind that can clear it; without it react-query hands back the in-flight
 * fetch and the row is only re-read on the next 30s poll.
 */
export function reresolveGroupAccount(
  qc: ReturnType<typeof useQueryClient>,
  groupId: string
): void {
  markGroupAccountUnresolved(groupId);
  qc.invalidateQueries(OPENER_GROUP_POD_STATE_KEY);
  qc.invalidateQueries(['opener-groups'], {}, { cancelRefetch: true });
}

/**
 * Start can decide WHICH TWO ACCOUNTS the group trades, and those live on the
 * `opener-groups` row (30s poll), not on the pod-state payload. So the group's
 * pair is marked unknowable before the request leaves and re-marked once it
 * settles — until the list has been re-read by a fetch that began after that,
 * every consumer resolves the group's accounts to `unresolved` instead of the
 * previous run's pair.
 */
export function useOpenerGroupStart() {
  const qc = useQueryClient();
  return useMutation(
    (vars: { groupId: string } & StartOpenerGroupOptions) =>
      startOpenerGroup(vars.groupId, {
        accounts: vars.accounts,
        liveMode: vars.liveMode,
        tier: vars.tier
      }),
    {
      mutationKey: 'entry:group-start',
      onMutate: (vars: { groupId: string }) => {
        markGroupAccountUnresolved(vars.groupId);
      },
      // Settled, not success: a start that errored may still have written the
      // account ids before failing, so the cached row is equally suspect.
      onSettled: (_data, _error, vars) => {
        reresolveGroupAccount(qc, vars.groupId);
      }
    }
  );
}

export function useOpenerGroupStop() {
  const qc = useQueryClient();
  return useMutation(stopOpenerGroup, {
    mutationKey: 'entry:group-stop',
    onMutate: (groupId: string) => {
      markGroupAccountUnresolved(groupId);
    },
    onSettled: (_data, _error, groupId) => {
      reresolveGroupAccount(qc, groupId);
      // Stop pauses the group's rules server-side.
      qc.invalidateQueries('entry-rules');
    }
  });
}

/** Soft panic — cancel + pause the group's rules; engine pod stays up. */
export function useOpenerGroupPanicSoft() {
  const qc = useQueryClient();
  return useMutation(panicSoftOpenerGroup, {
    mutationKey: 'entry:group-panic-soft',
    onSuccess: () => {
      qc.invalidateQueries(OPENER_GROUP_POD_STATE_KEY);
      qc.invalidateQueries('entry-rules');
    }
  });
}

/**
 * Hard panic — kill the group engine pod + cancel ALL the group's orders
 * externally from prediction-manager. No lockout: the group can be
 * restarted immediately.
 */
export function useOpenerGroupPanic() {
  const qc = useQueryClient();
  return useMutation(panicOpenerGroup, {
    mutationKey: 'entry:group-panic',
    onSuccess: () => {
      qc.invalidateQueries(OPENER_GROUP_POD_STATE_KEY);
      qc.invalidateQueries('entry-rules');
    }
  });
}
